import { useMemo, useState } from 'react';
import { Skeleton, Button, Box, Alert, AlertIcon } from '@chakra-ui/react';
import {
  getResourcePayload,
  transformResources,
  validateProductSelection,
} from 'features/context-ids/utils';
import { ResourceByProductType } from 'features/context-ids/types/types';
import { ProductDropdown } from './ProductDropdown';
import { SelectedProductRows } from './SelectedProductRows';
import { useGetAvailableProducts } from 'features/context-ids/api/useGetAvailableProducts';

export const TenantsByProductOptions = ({
  setValues,
}: {
  setValues: (prev: any) => any;
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [selectedProducts, setSelectedProducts] = useState<
    { productCode: string; tenants: ResourceByProductType[] }[]
  >([]);

  const { data, isLoading } = useGetAvailableProducts();
  const groupedTenantsByProductCode = transformResources(data?.data ?? []);

  const addProductHandler = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleProductSelect = (productCode: string) => {
    if (validateProductSelection(productCode, selectedProducts)) {
      setIsDropdownOpen(false);
      setErrorMessage('You can only select CDP or Personalize as a resource.');
      return;
    }

    const tenants =
      groupedTenantsByProductCode[
        productCode as keyof typeof groupedTenantsByProductCode
      ];
    setSelectedProducts([...selectedProducts, { productCode, tenants }]);
    setIsDropdownOpen(false);
    setErrorMessage(null);
  };

  const handleTenantSelect = (productCode: string, tenantIdWithEnv: string) => {
    const selectedResource = groupedTenantsByProductCode[
      productCode as keyof typeof groupedTenantsByProductCode
    ]
      //@ts-ignore
      ?.map((r: ResourceByProductType) => getResourcePayload(r))
      .find(
        ({ tenantId, environment }: ResourceByProductType) =>
          `${tenantId} ${environment ?? 'non-env'}` === tenantIdWithEnv,
      );

    if (selectedResource)
      setValues((prev: any) => {
        const existingResourceIndex = prev.resources.findIndex(
          (r: ResourceByProductType) =>
            r.productCode === selectedResource.productCode,
        );

        if (existingResourceIndex !== -1) {
          const updatedResources = [...prev.resources];
          updatedResources[existingResourceIndex] = selectedResource;
          return {
            ...prev,
            resources: updatedResources,
          };
        } else {
          return {
            ...prev,
            resources: [...prev.resources, selectedResource],
          };
        }
      });
    setErrorMessage(null);
  };

  const removeProductHandler = (index: number) => {
    const updatedSelectedProducts = selectedProducts.filter(
      (_, i) => i !== index,
    );
    setSelectedProducts(updatedSelectedProducts);
    setValues((prev: any) => {
      const updatedResources = prev.resources.filter(
        (_: any, i: number) => i !== index,
      );

      return {
        ...prev,
        resources: [...updatedResources],
      };
    });
    setErrorMessage(null);
  };

  const availableProducts = useMemo(
    () =>
      Object.keys(groupedTenantsByProductCode).filter(
        (productCode) =>
          !selectedProducts.some(
            (selected) => selected.productCode === productCode,
          ) &&
          groupedTenantsByProductCode[
            productCode as keyof typeof groupedTenantsByProductCode
            //@ts-ignore
          ]?.length,
      ),
    [groupedTenantsByProductCode, selectedProducts],
  );

  if (isLoading)
    return <Skeleton rounded={10} width={120} height={10} mt={4} />;
  return (
    <Box w='full' pb='12'>
      {errorMessage && (
        <Alert status='error' mb='4'>
          <AlertIcon />
          {errorMessage}
        </Alert>
      )}
      <SelectedProductRows
        selectedProducts={selectedProducts}
        removeProductHandler={removeProductHandler}
        handleTenantSelect={handleTenantSelect}
      />
      {availableProducts.length ? (
        <Box position='relative'>
          <Button
            type='button'
            variant='outline'
            size='md'
            onClick={addProductHandler}
            my='4'
            data-testid='add-product-button'
          >
            Add resource
          </Button>
          {isDropdownOpen && (
            <Box
              position='absolute'
              minW={200}
              zIndex='1'
              bg='white'
              boxShadow='md'
              borderRadius='md'
              mt='2'
              data-testid='product-dropdown'
            >
              <ProductDropdown
                availableProducts={availableProducts}
                handleProductSelect={handleProductSelect}
              />
            </Box>
          )}
        </Box>
      ) : null}
    </Box>
  );
};
